import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { environment } from '../environments/environment';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxSpinnerModule } from 'ngx-spinner';
import { AppRoutingModule } from './app-routing.module';
import { BlocksModule } from './blocks/blocks.module';
import { AppComponent } from './blocks/root/app.component';
import { AuthHttpInterceptor } from './core/common-services/interceptor.service';
import { LoadingInterceptor } from './core/common-services/loading.interceptor';
import { TimezoneInterceptor } from './core/common-services/time-zone-Interceptor';
import { ConfirmationDialogComponent } from './shared/components/confirmation-dialog/confirmation-dialog.component';
import { SharedModule } from './shared/shared/shared.module';
import { ConfirmationService, MessageService } from 'primeng/api';

// export function HttpLoaderFactory(http: HttpClient) {
//   return new TranslateHttpLoader(http, './assets/i18n/', '.json');
// }

/** Sentry's handler can recurse (detectChanges → new error); guard to avoid stack overflow. */
function createAppGlobalErrorHandler(env: typeof environment): ErrorHandler {
  if (!env.production) {
    return {
      handleError(error: unknown): void {
        console.error(error);
      },
    };
  }
  const sentry = Sentry.createErrorHandler({ showDialog: false });
  let handling = false;
  return {
    handleError(error: unknown): void {
      if (handling) {
        console.error(error);
        return;
      }
      handling = true;
      try {
        sentry.handleError(error);
      } finally {
        handling = false;
      }
    },
  };
}

@NgModule({
  declarations: [ConfirmationDialogComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    BlocksModule,
    RouterModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule,
    MatMenuModule,
    MatTableModule,
    BrowserAnimationsModule,
    NgSelectModule,

    NgxSpinnerModule.forRoot({
      type: 'ball-scale-multiple',
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslationLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],

  providers: [
    {
      provide: ErrorHandler,
      useFactory: (): ErrorHandler => createAppGlobalErrorHandler(environment),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: InitTranslation,
      deps: [TranslateService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
      // deps: [TranslateService],
    },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: TimezoneInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    ConfirmationService,
    MessageService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function TranslationLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
export function InitTranslation(translate: TranslateService) {
  return () => {
    const savedLang = localStorage.getItem('currentLang') || 'en';
    translate.setDefaultLang('en');
    translate.addLangs(['en', 'ar']);
    return firstValueFrom(translate.use(savedLang));
  };
}
